import { useState, useEffect } from "react";
import { motion, AnimatePresence, useScroll, useMotionValueEvent } from "framer-motion";
import { ArrowUpRight, X, Menu } from "lucide-react";
import { Logo } from "./Logo";
import { scrollToId } from "../lib/smooth";

const LINKS = [
    { label: "Profile", href: "#profile" },
    { label: "Epoxy Boxes", href: "#epoxy-box" },
    { label: "Process", href: "#process" },
    { label: "Machinery", href: "#machinery" },
    { label: "Products", href: "#products" },
    { label: "Industries", href: "#industries" },
];

const go = (e, href, close) => {
    e.preventDefault();
    if (close) close();
    setTimeout(() => scrollToId(href), close ? 80 : 0);
};

export const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const { scrollY } = useScroll();
    useMotionValueEvent(scrollY, "change", (v) => setScrolled(v > 40));

    useEffect(() => {
        document.body.style.overflow = open ? "hidden" : "";
        return () => {
            document.body.style.overflow = "";
        };
    }, [open]);

    return (
        <>
            <header
                className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
                    scrolled
                        ? "bg-[#09090b]/90 backdrop-blur-md border-b border-zinc-800"
                        : "bg-transparent border-b border-transparent"
                }`}
            >
                <div className="shell flex items-center justify-between h-[72px]">
                    <a
                        href="#top"
                        data-testid="nav-logo-link"
                        onClick={(e) => go(e, "#top")}
                        className="shrink-0"
                    >
                        <Logo compact={scrolled} />
                    </a>
                    <nav className="hidden lg:flex items-center gap-8">
                        {LINKS.map((l) => (
                            <a
                                key={l.href}
                                href={l.href}
                                data-testid={`nav-link-${l.href.slice(1)}`}
                                onClick={(e) => go(e, l.href)}
                                className="font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-400 hover:text-safety transition-colors"
                            >
                                {l.label}
                            </a>
                        ))}
                        <a
                            href="#enquiry"
                            data-testid="nav-cta-enquiry"
                            onClick={(e) => go(e, "#enquiry")}
                            className="font-display uppercase tracking-wider text-sm font-semibold bg-safety text-charcoal px-5 py-2.5 hover:bg-zinc-50 transition-colors flex items-center gap-2"
                        >
                            Send Enquiry <ArrowUpRight size={15} />
                        </a>
                    </nav>
                    <button
                        className="lg:hidden text-zinc-100 p-2 border border-zinc-800"
                        data-testid="nav-mobile-toggle"
                        aria-label="Toggle menu"
                        onClick={() => setOpen(!open)}
                    >
                        {open ? <X size={20} /> : <Menu size={20} />}
                    </button>
                </div>
            </header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        className="fixed inset-0 z-40 bg-[#09090b] lg:hidden flex flex-col justify-center"
                    >
                        <div className="safety-stripes h-1.5 absolute top-[72px] inset-x-0" />
                        <nav className="shell flex flex-col gap-2">
                            {LINKS.map((l, i) => (
                                <motion.a
                                    key={l.href}
                                    href={l.href}
                                    data-testid={`nav-mobile-link-${l.href.slice(1)}`}
                                    onClick={(e) => go(e, l.href, () => setOpen(false))}
                                    initial={{ opacity: 0, x: -24 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.06 * i, duration: 0.4 }}
                                    className="font-display text-4xl uppercase font-semibold text-zinc-100 hover:text-safety transition-colors py-2 border-b border-zinc-900 flex items-center justify-between"
                                >
                                    {l.label}
                                    <span className="font-mono text-xs text-safety">
                                        0{i + 1}
                                    </span>
                                </motion.a>
                            ))}
                            <motion.a
                                href="#enquiry"
                                data-testid="nav-mobile-cta-enquiry"
                                onClick={(e) => go(e, "#enquiry", () => setOpen(false))}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.4 }}
                                className="btn-solid mt-8 justify-center"
                            >
                                Send Enquiry <ArrowUpRight size={16} />
                            </motion.a>
                        </nav>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default Navbar;